/** Duck-typed view of the Axios error shape, so this module needn't import axios. */
interface ResponseCarryingError {
  response?: { data?: unknown };
  message?: unknown;
}

/** Duck-typed view of `NormalizedApiError` from `@smartsash/api-client`. */
interface KeyedApiError {
  messageKey: string;
  detail?: unknown;
}

function isKeyedApiError(error: unknown): error is KeyedApiError {
  return (
    error !== null &&
    typeof error === 'object' &&
    typeof (error as { messageKey?: unknown }).messageKey === 'string'
  );
}

import type { Translate } from './translate';

const BODY_MESSAGE_KEYS = ['error', 'message', 'detail', 'title'] as const;

function firstNonEmpty(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

/** Pull a message out of a v2 (`{ error }`) or ProblemDetails response body. */
function messageFromBody(data: unknown): string | undefined {
  if (typeof data === 'string') return firstNonEmpty(data);
  if (data && typeof data === 'object') {
    const body = data as Record<string, unknown>;
    for (const key of BODY_MESSAGE_KEYS) {
      const found = firstNonEmpty(body[key]);
      if (found) return found;
    }
  }
  return undefined;
}

/**
 * Extract a human-readable error message from an unknown error.
 *
 * Two error worlds coexist while the strangler runs (HS-847):
 *
 * - Shared layer — `customInstance` rejects with `NormalizedApiError`, which carries a
 *   stable i18n `messageKey` plus optional server `detail`. Server text wins; otherwise
 *   `translate` resolves the key, and without a translator the caller's `fallback` is used.
 *   Never read `.message` here: it is `detail ?? messageKey`, so a raw key such as
 *   `errors.validation` would land on screen.
 * - Legacy hand services — still reject with Axios errors. Their body is read structurally
 *   (`error`, `message`, `detail`, `title`) so this module imports no axios; apps/admin
 *   must import none at all (03-02 §5).
 *
 * Deliberately import-free. Pulling in the i18n instance would drag the i18n bootstrap into
 * every page test that partially mocks `react-i18next`, and importing `NormalizedApiError`
 * for an `instanceof` check would break the page tests that mock `@smartsash/api-client`
 * wholesale — as well as being fragile across duplicate module instances. Both errors are
 * therefore recognised structurally.
 */
export const getErrorMessage = (
  error: unknown,
  fallback: string,
  translate?: Translate,
): string => {
  if (isKeyedApiError(error)) {
    return firstNonEmpty(error.detail) ?? translate?.(error.messageKey) ?? fallback;
  }

  if (error && typeof error === 'object' && 'response' in error) {
    const { response, message } = error as ResponseCarryingError;
    return messageFromBody(response?.data) ?? firstNonEmpty(message) ?? fallback;
  }

  if (error instanceof Error) {
    return error.message || fallback;
  }

  return fallback;
};
